import { useEffect, useState } from "react";
import {
  toggleAuthenticator,
  updateLoginOtpChannel,
} from "../../services/SignUpReport/SignUpReport";
import { getAdminProfile } from "../../services/authService";
import {
  ArrowRightLeft,
  Loader2,
  Mail,
  ShieldCheck,
  Smartphone,
  Key,
} from "lucide-react";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

export default function LoginSecurity() {
  const [otpChannel, setOtpChannel] = useState("sms");
  const [isAuthenticatorActive, setIsAuthenticatorActive] = useState(false);
  const [profileData, setProfileData] = useState(null);

  // Independent loading states for smooth UX
  const [isChannelToggling, setIsChannelToggling] = useState(false);
  const [is2FAToggling, setIs2FAToggling] = useState(false);

  // Fetch admin profile and initial security settings
  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await getAdminProfile();
        setOtpChannel(response?.otp_channel);
        setProfileData(response);
        // Correctly read from response object directly
        setIsAuthenticatorActive(
          response?.google_authenticator?.is_globally_enabled ?? false,
        );
      } catch (error) {
        console.error("Error fetching profile details", error);
      }
    };

    getProfile();
  }, []);

  const handleToggleChannel = async () => {
    if (isChannelToggling) return;

    setIsChannelToggling(true);
    const targetChannel = otpChannel === "sms" ? "email" : "sms";

    try {
      const res = await updateLoginOtpChannel({
        channel: targetChannel,
      });

      if (res?.status?.code === 200 || res?.status_code === 200) {
        setOtpChannel(res?.data?.channel || targetChannel);
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      }
    } catch (error) {
      console.error("Error updating OTP channel:", error);
    }

    setIsChannelToggling(false);
  };

  const handleToggle2FA = async () => {
    if (is2FAToggling) return;

    setIs2FAToggling(true);
    const nextState = !isAuthenticatorActive;

    try {
      const res = await toggleAuthenticator({
        is_active: nextState,
      });

      if (res?.status?.code === 200 || res?.status_code === 200) {
        // Update state based on API response or fallback to intended next state
        setIsAuthenticatorActive(
          res?.data?.is_active ??
            res?.data?.google_authenticator?.is_globally_enabled ??
            nextState,
        );
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      } else {
        showErrorToast(res?.message || res?.data?.message);
      }
    } catch (error) {
      console.error("Error toggling authenticator:", error);
    }

    setIs2FAToggling(false);
  };

  const targetChannelText = otpChannel === "sms" ? "email" : "sms";

  return (
    <div className="bg-slate-50 font-sans min-h-screen flex flex-col items-center p-4 sm:p-8">
      <div className="w-full max-w-4xl space-y-6">
        {/* Header Container */}
        <div className="flex justify-between items-center gap-6 px-6 py-4 bg-white/70 backdrop-blur-xl border border-cyan-100 shadow-[0_4px_20px_-4px_rgba(6,182,212,0.1)] rounded-2xl">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-cyan-100/70 shadow-inner">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-cyan-500 tracking-tight">
              Login security
            </h1>
          </div>
        </div>

        {/* 1. OTP Settings Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-bold text-slate-800">OTP Settings</h2>
            <p className="text-sm text-slate-500 mt-1">
              Configure how users receive their verification codes during login.
            </p>
          </div>

          <div className="p-6 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-10">
            {/* Current State Display */}
            <div className="flex flex-col items-center justify-center space-y-3 p-8 w-full sm:w-1/2 bg-gradient-to-b from-slate-50 to-white rounded-2xl border border-slate-100">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Current Channel
              </p>
              <div className="flex items-center gap-3 text-3xl font-bold text-slate-800">
                {otpChannel === "sms" ? (
                  <>
                    <Smartphone className="w-8 h-8 text-blue-500" /> SMS
                  </>
                ) : (
                  <>
                    <Mail className="w-8 h-8 text-cyan-500" /> Email
                  </>
                )}
              </div>
            </div>

            {/* Action Area */}
            <div className="flex flex-col items-center justify-center space-y-6 w-full sm:w-1/2">
              <div className="text-center space-y-2">
                <p className="text-base text-slate-600">
                  Change Login OTP channel to{" "}
                  <strong className="text-cyan-600 font-semibold uppercase">
                    {targetChannelText}
                  </strong>
                </p>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">
                  Users will receive their verification codes via{" "}
                  {targetChannelText}.
                </p>
              </div>

              {/* Custom Toggle Switch */}
              <button
                onClick={handleToggleChannel}
                disabled={isChannelToggling}
                className={`
                  relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 
                  focus-visible:ring-cyan-500 focus-visible:ring-offset-2 shadow-inner
                  ${otpChannel === "email" ? "bg-cyan-500" : "bg-slate-300"}
                  ${isChannelToggling ? "opacity-80 cursor-not-allowed" : "hover:opacity-90"}
                `}
                role="switch"
                aria-checked={otpChannel === "email"}
              >
                <span className="sr-only">Toggle OTP Channel</span>

                <div className="absolute inset-0 flex items-center justify-between px-2 text-white pointer-events-none">
                  <Smartphone className="w-4 h-4 opacity-70" />
                  <Mail className="w-4 h-4 opacity-70" />
                </div>

                <span
                  className={`
                    pointer-events-none flex items-center justify-center h-9 w-9 transform rounded-full 
                    bg-white shadow-md ring-0 transition duration-300 ease-in-out absolute top-0
                    ${otpChannel === "email" ? "translate-x-10" : "translate-x-0"}
                  `}
                >
                  {isChannelToggling ? (
                    <Loader2 className="w-5 h-5 text-cyan-500 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="w-4 h-4 text-slate-400" />
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* 2. Google Authenticator Settings Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-bold text-slate-800">
              Google Authenticator (2FA)
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Enhance account security by requiring time-based one-time
              passwords from an authenticator app.
            </p>
          </div>

          <div className="p-6 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-10">
            {/* Current State Display */}
            <div className="flex flex-col items-center justify-center space-y-3 p-8 w-full sm:w-1/2 bg-gradient-to-b from-slate-50 to-white rounded-2xl border border-slate-100">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Authenticator Status
              </p>
              <div className="flex items-center gap-3 text-2xl font-bold text-slate-800">
                <Key
                  className={`w-8 h-8 ${isAuthenticatorActive ? "text-emerald-500" : "text-slate-400"}`}
                />
                <span
                  className={
                    isAuthenticatorActive
                      ? "text-emerald-600"
                      : "text-slate-500"
                  }
                >
                  {isAuthenticatorActive ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>

            {/* Action Area */}
            <div className="flex flex-col items-center justify-center space-y-6 w-full sm:w-1/2">
              <div className="text-center space-y-2">
                <p className="text-base text-slate-600">
                  {isAuthenticatorActive
                    ? "Google Authenticator is currently active."
                    : "Enable Google Authenticator for login verification."}
                </p>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">
                  {isAuthenticatorActive
                    ? "Your account is secured with 2FA authenticator codes."
                    : "Use apps like Google Authenticator or Authy to generate codes."}
                </p>
              </div>

              {/* 2FA Toggle Switch */}
              <button
                onClick={handleToggle2FA}
                disabled={is2FAToggling}
                className={`
                  relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 
                  focus-visible:ring-cyan-500 focus-visible:ring-offset-2 shadow-inner
                  ${isAuthenticatorActive ? "bg-emerald-500" : "bg-slate-300"}
                  ${is2FAToggling ? "opacity-80 cursor-not-allowed" : "hover:opacity-90"}
                `}
                role="switch"
                aria-checked={isAuthenticatorActive}
              >
                <span className="sr-only">Toggle Authenticator</span>

                <span
                  className={`
                    pointer-events-none flex items-center justify-center h-9 w-9 transform rounded-full 
                    bg-white shadow-md ring-0 transition duration-300 ease-in-out absolute top-0
                    ${isAuthenticatorActive ? "translate-x-10" : "translate-x-0"}
                  `}
                >
                  {is2FAToggling ? (
                    <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                  ) : (
                    <Key className="w-4 h-4 text-slate-400" />
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
